import { optionKey } from '@/utilities';

export type Routes = RouteGroup[];

export type RouteGroup = {
    key: string;
    label: string;
    items: RouteNode[];
};

type RouteNode = {
    key?: string;
    path?: string;
    aliases?: string[];
    creationModalShortcutString?: string;
    creationModalShortcutStringArray?: string[];

    // UI / navigation
    label?: string;

    // resource semantics
    resource?: string;
    handle?: {
        singular: string;
        plural: string;
    };

    // NEW: metadata config
    meta?: {
        includeInHierarchy?: boolean; // default true
        isEntity?: boolean; // marks this level as entity
    };

    // nesting
    items?: RouteNode[];
};

export const useRoutes = () => {
    const routes: Routes = [
        {
            key: 'workspaceResources',
            label: 'Workspace Resources',
            items: [
                {
                    path: '/',
                    label: 'Home',
                },
                {
                    path: '/inbox',
                    label: 'Inbox',
                },
                {
                    path: '/folders',
                    resource: 'folder',
                    creationModalShortcutString: `${optionKey} F`,
                    creationModalShortcutStringArray: [optionKey, 'F'],
                    handle: {
                        singular: 'Folder',
                        plural: 'Folders',
                    },
                },
                {
                    path: '/drafts',
                    label: 'Drafts',
                },
                {
                    path: '/archive',
                    label: 'Archive',
                },
                {
                    path: '/search',
                    label: 'Search',
                },
                {
                    path: '/investments',
                    resource: 'investment',
                    creationModalShortcutString: `${optionKey} I`,
                    creationModalShortcutStringArray: [optionKey, 'I'],
                    handle: {
                        singular: 'Investment',
                        plural: 'Investments',
                    },
                },
                {
                    path: '/memos',
                    resource: 'memo',
                    creationModalShortcutString: `${optionKey} M`,
                    creationModalShortcutStringArray: [optionKey, 'M'],
                    handle: {
                        singular: 'Memo',
                        plural: 'Memos',
                    },
                },
                {
                    path: '/pipelines',
                    resource: 'pipeline',
                    creationModalShortcutString: `${optionKey} P`,
                    creationModalShortcutStringArray: [optionKey, 'P'],
                    handle: {
                        singular: 'Pipeline',
                        plural: 'Pipelines',
                    },
                },
                // Lifecycles remains dormant until the product surface is ready to ship.
                {
                    path: '/settings/workspace/permissions/teams',
                    resource: 'team',
                    creationModalShortcutString: `${optionKey} T`,
                    creationModalShortcutStringArray: [optionKey, 'T'],
                    handle: {
                        singular: 'Team',
                        plural: 'Teams',
                    },
                    items: [
                        {
                            path: '/settings/workspace/permissions/teams',
                            label: 'Permissions',
                        },
                        {
                            path: '/settings/workspace/general',
                            label: 'Workspace Settings',
                        },
                    ],
                },
                {
                    path: '/relations/dashboards',
                    aliases: ['/dashboards'],
                    resource: 'dashboard',
                    creationModalShortcutString: `${optionKey} D`,
                    creationModalShortcutStringArray: [optionKey, 'D'],
                    handle: {
                        singular: 'Dashboard',
                        plural: 'Dashboards',
                    },
                },
                {
                    path: '/relations/lists',
                    aliases: ['/lists'],
                    resource: 'list',
                    creationModalShortcutString: `${optionKey} L`,
                    creationModalShortcutStringArray: [optionKey, 'L'],
                    handle: {
                        singular: 'List',
                        plural: 'Lists',
                    },
                },
                {
                    path: '/relations/tasks',
                    aliases: ['/tasks'],
                    resource: 'task',
                    creationModalShortcutString: `${optionKey} K`,
                    creationModalShortcutStringArray: [optionKey, 'K'],
                    handle: {
                        singular: 'Task',
                        plural: 'Tasks',
                    },
                },
                {
                    path: '/trash',
                    label: 'Trash',
                },
                {
                    path: '/settings',
                    label: 'Settings',
                },
            ],
        },
        {
            key: 'settings',
            label: 'settings',
            items: [
                {
                    key: 'accountSettings',
                    label: 'Account Settings',
                    items: [
                        {
                            path: '/investments',
                            resource: 'investment',
                            creationModalShortcutString: `${optionKey} I`,
                            creationModalShortcutStringArray: [optionKey, 'I'],
                            handle: {
                                singular: 'Investment',
                                plural: 'Investments',
                            },
                        },
                    ],
                },
            ],
        },
    ];

    // const routes: RouteObject[] = ;

    function routeMatches(pathname: string, routePath: string) {
        if (routePath === '/') {
            return pathname === '/';
        }

        return pathname === routePath || pathname.startsWith(`${routePath}/`);
    }

    function getRouteMeta(
        pathname: string,
        nodes: RouteNode[] = routes.flatMap((route) => route.items as RouteNode[]),
    ): RouteNode | undefined {
        for (const node of nodes) {
            const paths = [node.path, ...(node.aliases ?? [])].filter(Boolean) as string[];

            if (paths.some((path) => routeMatches(pathname, path))) {
                return node;
            }

            if (node.items) {
                const match: RouteNode | undefined = getRouteMeta(pathname, node.items);

                if (match) {
                    return match;
                }
            }
        }
    }

    type RouteMeta = {
        workspace: string; // required
        hierarchy: string[]; // required (can be empty)
        entity?: string; // optional
    };

    function formatRouteMeta(meta: RouteMeta): string {
        const { entity, hierarchy, workspace } = meta;

        const hierarchyPart = hierarchy.join(' › ');

        const leftSide = entity ? [entity, hierarchyPart].filter(Boolean).join(' › ') : hierarchyPart;

        return leftSide ? `${leftSide} · ${workspace}` : workspace;
    }

    type ResolvedRoute = {
        node: RouteNode;
        hierarchy: string[];
    };

    function resolveRoute(pathname: string, nodes: RouteNode[], trail: string[] = []): ResolvedRoute | null {
        for (const node of nodes) {
            const label = node.handle?.plural || node.label;

            const nextTrail = label ? [...trail, label] : trail;

            const paths = [node.path, ...(node.aliases ?? [])].filter(Boolean) as string[];

            if (paths.some((path) => routeMatches(pathname, path))) {
                if (!node.items) {
                    return { node, hierarchy: nextTrail };
                }

                const childMatch = resolveRoute(pathname, node.items, nextTrail);
                return childMatch || { node, hierarchy: nextTrail };
            }

            if (node.items) {
                const match = resolveRoute(pathname, node.items, nextTrail);
                if (match) {
                    return match;
                }
            }
        }

        return null;
    }

    function buildRouteMeta({
        pathname,
        workspace,
        entity,
    }: {
        pathname: string;
        workspace: string;
        entity?: string;
    }): string {
        const resolved = resolveRoute(pathname, routes);

        if (!resolved) {
            return workspace;
        }

        return formatRouteMeta({
            workspace,
            hierarchy: resolved.hierarchy,
            entity,
        });
    }

    return { routes, getRouteMeta, buildRouteMeta };
};
